import { format } from 'date-fns'

type Activity = {
    startDate: string
}

type ActivitiesByDate<T> = {
    [date: string]: T[]
}[]

/**
 * Group activities by date
 *
 * Takes an array of activities and returns an array of activities grouped by date
 *
 * @example
 * const activities = [
 *     { startDate: '2024-06-01T10:00:00Z' },
 *     { startDate: '2024-06-01T12:00:00Z' },
 *     { startDate: '2024-06-02T09:00:00Z' },
 * ]
 *
 * const activitiesByDate = groupActivitiesByDate(activities)
 *
 * console.log(activitiesByDate)
 *
 * // [
 * //     { '2024-06-01': [{ startDate: '2024-06-01T10:00:00Z' }, { startDate: '2024-06-01T12:00:00Z' }] },
 * //     { '2024-06-02': [{ startDate: '2024-06-02T09:00:00Z' }] },
 * // ]
 *
 * @param activities - Activities to group
 * @returns Activities grouped by date
 */
function groupActivitiesByDate<T extends Activity>(
    activities: T[],
): ActivitiesByDate<T> {
    if (!activities) {
        return []
    }

    return activities.reduce((activitiesByDate, activity) => {
        const date = format(new Date(activity.startDate), 'yyyy-MM-dd')

        const activitiesByDateIndex = activitiesByDate.findIndex(
            (el) => el[date],
        )

        if (activitiesByDateIndex !== -1) {
            activitiesByDate[activitiesByDateIndex][date].push(activity)
        } else {
            activitiesByDate.push({ [date]: [activity] })
        }

        return activitiesByDate
    }, [] as ActivitiesByDate<T>)
}

export default groupActivitiesByDate
